import { useRef } from 'react';
import { Tooltip } from '../../components/molecules/Tooltip';
import { AIStarsIcon, InfoIcon } from '../../components/icons';
import { useModalController } from '@/components/molecules/Modal/useModalController';
import { ShareWidgetModal } from '@/components/molecules/ShareWidgetModal';
import { IconType } from './types';
import { Widget } from '@/modules';
import { RiLightbulbLine } from '@remixicon/react';
import { WidgetActions } from './WidgetActions';
import { WidgetInsights } from './WidgetInsights';
import { WidgetRecommendations } from './WidgetRecommendations';
import { usePopoverController } from '@/components/molecules/Popover/usePopoverController';
import { useEditWidget } from '@/modules/widgets/context/EditWidgetContext';
import { useUser } from '@/modules/auth/context/UserContext';
import { canDeleteWidget, canEditWidget, canDuplicateWidget, canViewAIInsights, canViewAIRecommendations, canShareWidget } from '@/modules/widgets/utils/permissions';

import { WidgetSubHeader } from './WidgetSubHeader';
import { useWidgetFiltersController } from './WidgetFilters/useWidgetFiltersController';
import { IColumnDef } from './table';

interface WidgetHeaderProps {
  title: string;
  dateRangeLabel?: string;
  description?: string;
  widgetInfo?: string;
  iconType?: IconType;
  fullyAIGenerated?: boolean;
  showDeleteButton?: boolean;
  onRefineWidget?: () => void;
  widget: Widget;
  onWidgetDelete?: () => void;
  widgetData?: any;
  isHovered?: boolean;
  isMinimized?: boolean;
  onMinimizeToggle?: () => void;
  colSpan?: number;
  allowInsights?: boolean;
  allowEdit?: boolean;
  widgetFiltersController: ReturnType<typeof useWidgetFiltersController>;
  onColumnReorder: (columns: IColumnDef[]) => void;
  onColumnChangeVisibility?: (columns: IColumnDef[]) => void;
  onMetricChangeVisibility?: (metricName: string) => void;
  chartMetrics?: { name: string; dataKey: string; color: string }[];
  hiddenMetrics?: Set<string>;
  /** Headers currently hidden in the table (synced with column picker + API `hidden`). */
  hiddenColumns?: Set<string>;
  showExportButton?: boolean;
  onExportClick?: (widget: Widget) => void;
  isExportLoading?: boolean;
}

export const WidgetHeader = ({
  title,
  dateRangeLabel,
  widgetInfo,
  iconType,
  fullyAIGenerated = false,
  showDeleteButton = false,
  showExportButton = false,
  onExportClick,
  isExportLoading,
  widget,
  onWidgetDelete,
  widgetData,
  isHovered = false,
  isMinimized = false,
  onMinimizeToggle,
  colSpan,
  allowInsights = true,
  allowEdit = true,
  widgetFiltersController,
  onColumnReorder,
  onColumnChangeVisibility,
  onMetricChangeVisibility,
  chartMetrics,
  hiddenMetrics,
  hiddenColumns
}: WidgetHeaderProps) => {
  const insightsController = usePopoverController();
  const recommendationsController = usePopoverController();
  const shareController = useModalController();
  const actionsMenuRef = useRef<HTMLDivElement>(null);
  const { openEditDrawer, openDuplicateDrawer } = useEditWidget();
  const { user, hydrated } = useUser();


  // Check if user can edit this widget
  const canEdit = hydrated ? canEditWidget(widget, user) && allowEdit : true;
  const canDelete = hydrated ? canDeleteWidget(widget, user) : true;
  const canDuplicate = hydrated ? canDuplicateWidget(widget, user) : false;
  const canShare = hydrated ? canShareWidget(widget, user) : false;
  const showInsights = allowInsights && hydrated && canViewAIInsights(widget, user);
  const showRecommendations = allowInsights && hydrated && canViewAIRecommendations(widget, user);

  const exportData = widgetData?.data;
  const isExportDisabled =
    exportData === null ||
    exportData === undefined ||
    (typeof exportData === 'object' && Object.keys(exportData).length === 0) ||
    (Array.isArray(exportData) && exportData.length === 0);

  return (
    <div className={`relative z-10 transition-colors py-6 px-4 ${isMinimized ? 'rounded-[16px]' : 'rounded-t-2xl'}`}>

      <div className="flex items-center justify-between relative">

        <div className="flex items-center min-w-0 shrink">
          {/*
          <div className="bg-widget-icon-bg p-2 rounded-lg mr-3">
            <WidgetIcon iconType={iconType} className="text-widget-icon" />
          </div>
          */}
          <div className="flex flex-col gap-0 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <h2
                className={`text-lg font-semibold text-widget-title transition-all duration-200 ml-4 truncate ${colSpan === 1 ? 'max-w-[120px]' : ''
                  }`}
                style={{ fontFamily: 'Roboto, sans-serif' }}
                title={title}
              >
                {colSpan === 1 && title.length > 7 ? `${title.substring(0, 7)}...` : title}
              </h2>
              {fullyAIGenerated && (
                <AIChip />
              )}
              {widgetInfo && (
                <Tooltip content={widgetInfo} variant="widgetTitle">
                  <InfoIcon />
                </Tooltip>
              )}
            </div>
            {/* {dateRangeLabel ? <div className="text-xs text-slate-500 leading-4 ml-4">{dateRangeLabel}</div> : null} */}
          </div>
        </div>

        <div className={`flex items-center gap-1 transition-opacity duration-200 mr-1 opacity-100 shrink-0 ml-auto`}>

          {!isMinimized &&
            <WidgetSubHeader
              widget={widget}
              widgetData={widgetData}
              onColumnReorder={onColumnReorder}
              onColumnChangeVisibility={onColumnChangeVisibility}
              onMetricChangeVisibility={onMetricChangeVisibility}
              widgetFiltersController={widgetFiltersController}
              chartMetrics={chartMetrics}
              hiddenMetrics={hiddenMetrics}
              hiddenColumns={hiddenColumns}
              isExportLoading={isExportLoading}
            />
          }

          {showInsights && (
            <Tooltip className='inline-flex' content="Get Insights">
              <button
                onClick={insightsController.open}
                className="p-0.5 rounded-full hover:shadow-md transition-all duration-200 cursor-pointer inline-flex items-center"
                style={{ color: '#1F8894' }}
              >
                <AIStarsIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          {showRecommendations && (
            <Tooltip className='inline-flex' content="Get Recommendations">
              <button
                onClick={recommendationsController.open}
                className="p-0.5 rounded-full hover:shadow-md transition-all duration-200 cursor-pointer inline-flex items-center"
                style={{ color: '#1F8894' }}
              >
                <RiLightbulbLine className="w-4 h-4" />
              </button>
            </Tooltip>
          )}
          <WidgetActions
            ref={actionsMenuRef}
            widget={widget}
            widgetData={widgetData}
            showExportButton={showExportButton}
            onExportClick={onExportClick}
            isExportLoading={isExportLoading}
            isExportDisabled={isExportDisabled}
            showDeleteButton={showDeleteButton && canDelete}
            showEditButton={canEdit}
            showDuplicateButton={canDuplicate}
            showShareButton={canShare}

            onDelete={onWidgetDelete}
            onEditClick={() => openEditDrawer(widget)}
            onDuplicateClick={() => openDuplicateDrawer(widget)}
            onShareClick={shareController.open}
            isMinimized={isMinimized}
            onMinimizeToggle={onMinimizeToggle}
          />
          <ShareWidgetModal widget={widget} controller={shareController} />
        </div>

        {showInsights && (
          <WidgetInsights
            widget={widget}
            widgetData={widgetData}
            externalController={insightsController}
            anchorRef={actionsMenuRef}
            hideTrigger={true}
          />
        )}

        {showRecommendations && (
          <WidgetRecommendations
            widget={widget}
            widgetData={widgetData}
            externalController={recommendationsController}
            anchorRef={actionsMenuRef}
            hideTrigger={true}
          />
        )}
      </div>
    </div>
  );
};


const AIChip = () => {
  return (
    <div className="absolute right-0 top-0 translate-y-[-30px]">
      <div className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-400 text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-sm">
        <span className="flex items-center gap-1">
          <AIStarsIcon color={'#fff'} />
          <span className={"ml-1"}>
            Fully AI Generated
          </span>
        </span>
      </div>
    </div>
  );
};